import { SmlsButton, SmlsInputText } from "@smiles/smiles-ui-kit-react";
import React, { useEffect, useState } from "react";
import { Helpers } from "@smiles/core-smiles";
import ReCAPTCHA from "react-google-recaptcha";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setConstantValue } from "typescript";
import { IconInformationCircle } from "../../assets/IconInformationCircle";
import { TangInputPassword } from "../../components/TangInputPassword";
import { constants, init } from "../../../services/core/config";
import { ILoginTypes, validateDocumentStatus } from "../../../services/core/services";
import { useLoginModalFlow, LoginModal } from "../../context/LoginModalFlowContext";
import useLoginTries from "../../hooks/useLoginTries";
import { getLink } from "../../../utils";
import { setRedirectEnvironmentInLocalStorage } from "../../utils/redirectEnviroment";

const FIRST_ACCESS_STATUS = [
  'INCOMPL. REGISTRATION PARTNER',
  'PENDING ENROLLMENT',
  'PRE REGISTRATION BANKS',
];

type ValidType = 'EMAIL' | 'CPF' | 'MEMBER';

interface WebAuthProps {
  code: string;
}

export function LeftSidebar() {
  const databaseConnection = 'smilesdb';

  const { t } = useTranslation('acesso');

  const [searchParams] = useSearchParams();
  const sourceAccess = searchParams.get('src')?.toLowerCase() || 'smiles';
  const sourceIsGolBrand = sourceAccess === 'gol';
  const automationBypass = !!searchParams.get('bpr');

  const authClient = searchParams.get('client') || '';

  useEffect(() => {
    setRedirectEnvironmentInLocalStorage({
      sourceAccess,
      authClient
    })
  }, [searchParams]);

  const loginModalFlow = useLoginModalFlow();
  const loginTries = useLoginTries();

  const [isWifiLogin, setIsWifiLogin] = useState<boolean>(false);

  console.log('[debug] isWifiLogin', isWifiLogin);

  const [identifier, setIdentifier] = useState<string>("");
  const [identifierType, setIdentifierType] = useState<string>('');
  const [pass, setPass] = useState<string>("");
  const [firstRecaptchaValue, setFirstRecaptchaValue] = useState<string>("");
  const [secondRecaptchaValue, setSecondRecaptchaValue] = useState<string>("");

  const [isContinueButtonLoading, setIsContinueButtonLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [passwordClear, setPasswordClear] = useState<boolean>(false);

  const [isPasswordInputVisible, setIsPasswordInputVisible] = useState<boolean>(false);
  const [isContinueButtonVisible, setIsContinueButtonVisible] = useState<boolean>(true);
  const [isSubmitButtonVisible, setIsSubmitButtonVisible] = useState<boolean>(false);
  const [isFirstRecaptchaVisible, setIsFirstRecaptchaVisible] = useState<boolean>(false);
  const [isSecondRecaptchaVisible, setIsSecondRecaptchaVisible] = useState<boolean>(false);
  const [needSecondRecaptcha, setNeedSecondRecaptcha] = useState<boolean>(false);

  const hasPass = !!pass && pass.length >= 4;

  const canContinue = (!!firstRecaptchaValue || automationBypass) && !!identifier && !!identifierType;
  const canSubmit = (!!firstRecaptchaValue || !!secondRecaptchaValue || automationBypass) && !!identifier && !!identifierType && hasPass;

  const [identifierErrorMessage, setIdentifierErrorMessage] = useState<string>("");
  const [hasIdentifierError, setHasIdentifierError] = useState<boolean>(false);

  const errorMessages: Record<ValidType, string> = {
    EMAIL: t('smiles_01_a_04.input_errors.email'),
    MEMBER: t('smiles_01_a_04.input_errors.member'),
    CPF: t('smiles_01_a_04.input_errors.document'),
  }

  const handleIdentifierErrorMessage = (type: ValidType): string => {
    return errorMessages[type];
  };

  useEffect(() => {
    if(hasIdentifierError) {
      setIdentifierErrorMessage(handleIdentifierErrorMessage(identifierType as ValidType));
    }
  }, [t, hasIdentifierError, identifierType])

  useEffect(() => {
    init();

    try {
      const decodedString = new TextDecoder().decode(Uint8Array.from(atob(window.auth0config), c => c.charCodeAt(0)));
      const config = JSON.parse(decodedString);

      const isWifi = config.dict.signin.title === 'spa-uv-wifi';
      setIsWifiLogin(isWifi);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('fail to get config: ', error);
    }
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Submit Event', event);
    throw new Error('sample');
  };

  const handleIdentifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setIdentifier(input);
    setIsFirstRecaptchaVisible(false);
    setHasIdentifierError(false);
  }

  const handleIdentifierValidate = (inputText: string): boolean => {
    let input = inputText;

    if (!Number.isNaN(Number(input.replace(/[.-]+/g, '')))) {
      input = inputText.replace(/[.-]+/g, '');
    }

    const validation = Helpers.validations.isValidLogin(input);

    const { isValid, ...rest } = validation;

    const identifierMask = rest?.mask || input;

    const type = validation.type || '';

    setIdentifierType(type);

    if (isValid && !isSecondRecaptchaVisible) {
      setIsFirstRecaptchaVisible(true);
      setIdentifier(identifierMask);
    }

    const hasError = !isValid && !!type;

    if (hasError) {
      setHasIdentifierError(true);
    }

    return !hasError;
  }

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setPass(input);
  }

  const allowPasswordInput = () => {
    setIsFirstRecaptchaVisible(false);

    setIsContinueButtonLoading(false);
    setIsContinueButtonVisible(false);
    setIsSubmitButtonVisible(true);
    setIsPasswordInputVisible(true);

    if (automationBypass) {
      setFirstRecaptchaValue('bpr');
      setSecondRecaptchaValue('bpr');
      setIsSecondRecaptchaVisible(false);
    }
  }

  const handleContinueButton = async () => {
    setIsContinueButtonLoading(true);

    let memberIdentifier = identifier;

    if (identifierType === 'CPF') {
      memberIdentifier = memberIdentifier.replace(/[.-]+/g, '')
    }

    try {
      const response = await validateDocumentStatus({
        recaptcha: firstRecaptchaValue,
        type: identifierType as ILoginTypes,
        value: memberIdentifier,
      });

      if (!response.data || !response.success) {
        loginModalFlow.openModal(LoginModal.ACCOUNT_NOT_FOUND);
        setIsPasswordInputVisible(false);
        setIsContinueButtonLoading(false);
        setFirstRecaptchaValue('');
        loginTries.setTries(0);
        window.grecaptcha.reset();
        return;
      }

      const passwordTries = response.data.countPasswordLock || 0;
      loginTries.setTries(passwordTries);

      if(passwordTries > 4) {
        loginModalFlow.openModal(LoginModal.PASSWORD_BLOCKED);
        setIsPasswordInputVisible(false);
        setFirstRecaptchaValue('');
        setSecondRecaptchaValue('');
        setIsContinueButtonLoading(false);
        window.grecaptcha.reset();
        return;
      }

      const memberData = response.data;

      const isCaduLogin = !memberData?.lastLogin && !memberData.acceptTerm && memberData.first && memberData.sourceBrand === 1;

      if(isCaduLogin) {
        loginModalFlow.openModal(LoginModal.FIRST_ACCESS);
        setIsContinueButtonLoading(false);
        return;
      }

      const preRegistration = {
        openedSr: response.data.openedSr || false,
        realStatus: response.data.realStatus || '',
      }

      const isPreRegistration = preRegistration.openedSr && ['INCOMPL. REGISTRATION PARTNER', 'PENDING ENROLLMENT'].includes(preRegistration.realStatus);

      if (isPreRegistration) {
        loginModalFlow.openModal(LoginModal.ACCOUNT_UNDER_REVIEW);
        setIsPasswordInputVisible(false);
        setFirstRecaptchaValue('');
        setIsContinueButtonLoading(false);
        window.grecaptcha.reset();
        return;
      }

      const isFirstAccess = !response.data?.lastLogin && FIRST_ACCESS_STATUS.includes(preRegistration.realStatus);

      if (isFirstAccess) {
        loginModalFlow.openModal(LoginModal.PRE_REGISTER, () => {
          allowPasswordInput();
        });
        
        setIsPasswordInputVisible(false);
        setIsContinueButtonLoading(false);
        return;
      }

      allowPasswordInput();
    } catch (err) {
      setFirstRecaptchaValue('');
      setIsContinueButtonLoading(false);
      window.grecaptcha.reset();
      console.log('[validateDocumentStatus] An error has occurred');
    }
  }

  const handleRecaptchaChange = (token: string | null) => {
    setFirstRecaptchaValue(token || '');
  }

  const handleRecaptchaExpired = () => {
    setFirstRecaptchaValue('');
  }

  const handleSecondRecaptchaChange = (token: string | null) => {
    setSecondRecaptchaValue(token || '');
    setFirstRecaptchaValue(token || '');
  }

  const handleSecondRecaptchaExpired = () => {
    setSecondRecaptchaValue('');
  }

  const handleSubmitButton = () => {
    setIsSubmitting(true);

    let memberIdentifier = identifier;

    if (identifierType === 'CPF') {
      memberIdentifier = memberIdentifier.replace(/[.-]+/g, '')
    }

    window.webAuth.login({
      realm: databaseConnection,
      username: memberIdentifier,
      password: pass,
      captcha_smiles: firstRecaptchaValue,
      connection_scope: 'http://auth0.com/oauth/grant-type/password-realm',
    }, (err: WebAuthProps) => {
      console.log(err);

      if (String(err.code).startsWith('invalid_user_password')) {
        const updatedTries = loginTries.tries + 1;
        loginTries.increaseTries();

        setNeedSecondRecaptcha(true);
        setIsSecondRecaptchaVisible(true);
        setSecondRecaptchaValue('');

        setIsFirstRecaptchaVisible(false);
        setFirstRecaptchaValue('');

        window.grecaptcha.reset();

        if(updatedTries >= 5) {
          setIdentifier('');
        }

        if([0, 1, 2, 3].includes(updatedTries)) {
          loginModalFlow.openModal(LoginModal.INCORRECT_DATA);
        } else if (updatedTries === 4) {
          loginModalFlow.openModal(LoginModal.PASSWORD_LAST_CHANCE);
        } else {
          loginModalFlow.openModal(LoginModal.PASSWORD_BLOCKED);
          setIsPasswordInputVisible(false);
          setFirstRecaptchaValue('');
          setSecondRecaptchaValue('');
          window.grecaptcha.reset();
        }
      } else {
        loginModalFlow.openModal(LoginModal.ERROR_GENERIC);
        window.grecaptcha.reset();
        setIsFirstRecaptchaVisible(false);
        setFirstRecaptchaValue('');

        setNeedSecondRecaptcha(true);
        setIsSecondRecaptchaVisible(true);
        setSecondRecaptchaValue('');
      }

      setPass('');
      setPasswordClear(!passwordClear);
      setIsSubmitting(false);
    })
  }

  const handleForgotPasswordClick = () => {
    window.location.href = getLink('recuperar/senha');
  }

  const handleCreateAccountClick = () => {
    window.location.href = getLink('cadastro');
  }

  return (
    <div className="left-sidebar-container">
      <div className="left-box-wrapper">
        <h2 className="title">{t('smiles_01_a_04.title')}</h2>
        <p className="description">{t('smiles_01_a_04.text')}</p>
        <form onSubmit={handleSubmit}>
          <div className="identifier-input">
            <SmlsInputText
              inputMode="text"
              id="identifier"
              textLabel={t('smiles_01_a_04.input')}
              placeholder=""
              value={identifier}
              debouncedTime={700}
              onChange={handleIdentifierChange}
              validate={handleIdentifierValidate}
              errorMessage={identifierErrorMessage}
            />
          </div>

          {isPasswordInputVisible && (
            <div className="password-input">
              <TangInputPassword onlyNumbers textLabel={t('smiles_05.input')} onChange={handlePasswordChange} flipFlopClear={passwordClear} />
            </div>
          )}

          {
            sourceIsGolBrand && isContinueButtonVisible && (
              <div className="warning-gol-origin">
                <IconInformationCircle />
                <p>{t('gol_01_a_03.text')}</p>
              </div>
            )
          }

          {
            sourceIsGolBrand && isSubmitButtonVisible && (
              <div className="warning-gol-origin">
                <IconInformationCircle />
                <p>
                  {t('gol_04_a_05.text')}
                </p>
              </div>
            )
          }

          {
            isFirstRecaptchaVisible && (
              <div className="first-recatpcha">
                <ReCAPTCHA
                  sitekey={constants.recaptcha}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                />
              </div>
            )
          }

          {
            needSecondRecaptcha && isSecondRecaptchaVisible && (
              <div className="first-recatpcha">
                <ReCAPTCHA
                  sitekey={constants.recaptcha}
                  onChange={handleSecondRecaptchaChange}
                  onExpired={handleSecondRecaptchaExpired}
                />
              </div>
            )
          }

          <div className="bottom-buttons">
            <SmlsButton text={t('smiles_01_a_04.button_secondary')} color="hyperlink" onClick={handleForgotPasswordClick} />

            {isContinueButtonVisible &&
              <SmlsButton disabled={!canContinue} isLoading={isContinueButtonLoading} text={t('smiles_01_a_04.button_primary')} onClick={handleContinueButton} />}
            {isSubmitButtonVisible &&
              <SmlsButton disabled={!canSubmit} text={t('smiles_01_a_04.button_primary')} isLoading={isSubmitting} onClick={handleSubmitButton} />}
          </div>
        </form>
      </div>
      <div className="footer-create-account">
        <p>{t('smiles_01_a_04.text2')}</p>
        <SmlsButton text={t('smiles_01_a_04.button_secondary2')} color="hyperlink" onClick={handleCreateAccountClick} />
      </div>
    </div>
  )
}